import * as THREE from 'three';
import type { TowerType } from '../assets/modelFactories/TowerFactory';

export class InputController {
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly intersection = new THREE.Vector3();

  selectedTower: TowerType | null = null;
  hoverPosition = new THREE.Vector3();
  private isHovering = false;
  private confirmedPosition: THREE.Vector3 | null = null;
  private speedToggle = false;

  private readonly onPointerMove = (event: PointerEvent) => {
    this.updatePointer(event.clientX, event.clientY);
  };

  private readonly onPointerDown = (event: PointerEvent) => {
    if (event.target instanceof HTMLElement && event.target.closest('#tower-panel, #overlay, #touch-controls')) {
      return;
    }
    this.updatePointer(event.clientX, event.clientY);
    if (this.selectedTower && this.isHovering) {
      this.confirmedPosition = this.hoverPosition.clone();
    }
  };

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Digit1') this.selectTower('mg');
    if (event.code === 'Digit2') this.selectTower('cannon');
    if (event.code === 'Digit3') this.selectTower('at');
    if (event.code === 'Escape') this.selectTower(null);
    if (event.code === 'Space') {
      this.speedToggle = !this.speedToggle;
      this.onSpeedToggle(this.speedToggle);
    }
  };

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly onSelectTower: (type: TowerType | null) => void,
    private readonly onSpeedToggle: (active: boolean) => void,
  ) {
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('keydown', this.onKeyDown);
  }

  update(): void {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster.ray.intersectPlane(this.plane, this.intersection);
    this.isHovering = hit !== null;
    if (hit) {
      this.hoverPosition.copy(this.intersection);
    }
  }

  consumePlacement(): { type: TowerType; position: THREE.Vector3 } | null {
    if (!this.confirmedPosition || !this.selectedTower) return null;
    const result = { type: this.selectedTower, position: this.confirmedPosition.clone() };
    this.confirmedPosition = null;
    return result;
  }

  consumeSpeedToggle(): boolean | null {
    return null;
  }

  selectTower(type: TowerType | null): void {
    this.selectedTower = type;
    this.onSelectTower(type);
  }

  toggleSpeed(): void {
    this.speedToggle = !this.speedToggle;
    this.onSpeedToggle(this.speedToggle);
  }

  isSpeedActive(): boolean {
    return this.speedToggle;
  }

  setSpeedActive(active: boolean): void {
    this.speedToggle = active;
  }

  private updatePointer(clientX: number, clientY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  }

  dispose(): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('keydown', this.onKeyDown);
  }
}
